import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../Components/Navbar";
import SearchBar from "../Components/SearchBar";
import HelpMessage from "../Components/HelpMessage";
import { toast } from "react-toastify";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const buildEmptyValues = () => Array(months.length).fill("");

const getStoredSupplierDetails = () => ({
  code: localStorage.getItem("currentUser")?.trim() || "",
  name: localStorage.getItem("supplierName")?.trim() || "",
});

const Profile = () => {
  const [{ code: supplierCode, name: supplierName }, setSupplierDetails] =
    useState(getStoredSupplierDetails);

  const [values, setValues] = useState(buildEmptyValues);
  const [initialValues, setInitialValues] = useState(buildEmptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState("");

  const handleSupplierChange = useCallback((event) => {
    const nextCode = event?.detail?.supplier?.trim() || getStoredSupplierDetails().code;
    const nextName = event?.detail?.name?.trim() || getStoredSupplierDetails().name;
    setSupplierDetails({ code: nextCode, name: nextName });
  }, []);

  useEffect(() => {
    window.addEventListener("supplierContextChanged", handleSupplierChange);
    return () => {
      window.removeEventListener("supplierContextChanged", handleSupplierChange);
    };
  }, [handleSupplierChange]);

  const handleInputChange = (index, value) => {
    const numericValue = value === "" ? "" : Number(value);
    const nextValues = [...values];
    nextValues[index] = Number.isNaN(numericValue) ? "" : numericValue;
    setValues(nextValues);
  };

  const constructPayload = () =>
    months.reduce((acc, month, index) => {
      const numeric = Number(values[index]);
      if (!Number.isNaN(numeric) && values[index] !== "") {
        acc.push({ month, value: numeric });
      }
      return acc;
    }, []);

  const fetchProfileData = useCallback(
    async (code) => {
      if (!code) {
        setValues(buildEmptyValues());
        setInitialValues(buildEmptyValues());
        setLoading(false);
        setError("");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASEURL}/api/profile/${encodeURIComponent(code)}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(response.statusText);
        }

        const data = await response.json();
        const nextValues = buildEmptyValues();
        data.forEach((entry) => {
          const monthIndex = months.indexOf(entry?.month?.trim());
          if (monthIndex !== -1) {
            nextValues[monthIndex] = entry?.vol ?? "";
          }
        });
        setValues(nextValues);
        setInitialValues(nextValues);
        setEditMode(false);
      } catch (err) {
        console.error("Error fetching profile data:", err);
        setError("Unable to load profile data right now.");
        setValues(buildEmptyValues());
        setInitialValues(buildEmptyValues());
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchProfileData(supplierCode);
  }, [fetchProfileData, supplierCode]);

  const handleToggleEdit = () => {
    if (editMode) {
      setValues(initialValues);
      setEditMode(false);
    } else {
      setEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setValues(initialValues);
    setEditMode(false);
  };

  const handleSaveData = async () => {
    if (!supplierCode) {
      toast.error("Select a supplier before saving.");
      return;
    }

    const payload = constructPayload();
    if (payload.length === 0) {
      toast.info("Enter at least one monthly value before saving.");
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          supplier: supplierCode,
          year: new Date().getFullYear(),
          month: payload,
        }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      setInitialValues(values);
      setEditMode(false);
      toast.success("Profile data saved successfully.");
    } catch (err) {
      console.error("Error saving profile data:", err);
      toast.error("Unable to save profile data.");
    } finally {
      setSaving(false);
    }
  };

  const pageSubtitle = useMemo(() => {
    if (!supplierCode) {
      return "Select a supplier to view weighted volume expectations.";
    }
    return `Set the expected weighted volume for each month for supplier ${supplierName || supplierCode}.`;
  }, [supplierCode, supplierName]);

  return (
    <div className="customer-layout">
      <Navbar />
      <main className="customer-main">
        <section className="card profile-card">
          <header className="card-header profile-header">
            <div className="profile-header__titles">
              <h1 className="page-hero__title">Profile</h1>
              <p className="page-subtitle">{pageSubtitle}</p>
            </div>
            <div className="profile-actions profile-actions--header">
              <button
                type="button"
                className="button secondary sm"
                onClick={handleToggleEdit}
                disabled={saving || !supplierCode}
              >
                {editMode ? "Close edit" : "Edit"}
              </button>
              <button
                type="button"
                className="button ghost sm"
                onClick={handleCancelEdit}
                disabled={!editMode || saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button sm"
                onClick={handleSaveData}
                disabled={!editMode || saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </header>

          {error && <div className="alert error">{error}</div>}

          {loading ? (
            <div className="notes-empty">Loading profile data…</div>
          ) : (
            <div className="profile-table-wrapper">
              <table className="uds-table profile-table">
                <thead>
                  <tr>
                    <th scope="col">Month</th>
                    <th scope="col">Weighted Volume (L)</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((month, index) => (
                    <tr key={month}>
                      <td>{month}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={values[index]}
                          onChange={(event) => handleInputChange(index, event.target.value)}
                          disabled={!editMode || saving}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

            </div>
          )}
        </section>

        <div className="home-help">
          <HelpMessage />
        </div>
      </main>
    </div>
  );
};

export default Profile;
